import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Container, 
  Heading, 
  Text, 
  VStack, 
  useColorModeValue,
  Spinner,
  Alert,
  AlertIcon,
  Flex,
  Button,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { 
  subscriptionAtom, 
  subscriptionLoadingAtom, 
  subscriptionErrorAtom,
  fetchSubscriptionAtom,
  fetchSubscriptionPricesAtom,
  createCheckoutSessionAtom,
} from '../../../atoms/v0/subscriptionAtoms';
import { PageLayout } from '../../../components/v0/PageLayout';
import SubscriptionPlans from '../../../components/v0/subscription/SubscriptionPlans';
import SubscriptionManagement from '../../../components/v0/subscription/SubscriptionManagement';
import { SubscriptionStatus } from '../../../constants/subscription';

/**
 * 订阅页面
 * 显示订阅计划选项和当前订阅状态
 */
const Subscription: React.FC = () => {
  const { t } = useTranslation(['v0/subscription', 'v0/common']);
  const fetchSubscription = useSetAtom(fetchSubscriptionAtom);
  const fetchPrices = useSetAtom(fetchSubscriptionPricesAtom);
  const createCheckoutSession = useSetAtom(createCheckoutSessionAtom);
  const isLoading = useAtomValue(subscriptionLoadingAtom);
  const error = useAtomValue(subscriptionErrorAtom);
  const subscriptionData = useAtomValue(subscriptionAtom);
  const bgColor = useColorModeValue('#FFFFFF', '#1C1C1E');
  const borderColor = useColorModeValue('#EBEBEB', '#2C2C2E');
  
  // 在组件挂载时获取订阅信息和价格信息
  useEffect(() => {
    fetchSubscription();
    fetchPrices();
  }, [fetchSubscription, fetchPrices]);
  
  // 处理订阅计划选择
  const handleSelectPlan = async (priceId: string) => {
    if (!priceId) {
      console.error('Invalid Price ID received');
      return;
    }
    
    try {
      await createCheckoutSession(priceId);
    } catch (error) {
      console.error('Failed to create checkout session:', error);
    }
  };
  
  // 是否有活跃订阅或者已取消但未过期的订阅
  const hasActiveSubscription = subscriptionData && (
    // 状态为活跃或试用
    [SubscriptionStatus.ACTIVE, SubscriptionStatus.TRIALING].includes(subscriptionData.status as any) ||
    // 或者状态为取消但未过期（仍可使用）
    (subscriptionData.status === SubscriptionStatus.CANCELED && 
     new Date(subscriptionData.currentPeriodEnd) > new Date())
  );
  
  return (
    <PageLayout title={t('v0/subscription:pageTitle')}>
      <Box bg={bgColor} minH="calc(100vh - 64px)" py={{ base: 4, md: 8 }}>
        <Container maxW={{ base: '100%', md: '90%', lg: '900px' }} px={{ base: 4, md: 6 }}>
          <VStack spacing={{ base: 6, md: 10 }} align="stretch">
            {/* 标题部分 */}
            <Box textAlign="center" py={{ base: 6, md: 10 }}>
              <Heading 
                as="h1" 
                fontSize={{ base: '3xl', md: '4xl', lg: '5xl' }}
                mb={{ base: 3, md: 4 }}
                fontWeight="500"
              >
                {t('v0/subscription:hero.title')}
              </Heading>
              <Text 
                fontSize={{ base: 'md', md: 'lg' }}
                maxW="container.md" 
                mx="auto"
                color={useColorModeValue('#767676', '#A0A0A0')}
              >
                {t('v0/subscription:hero.subtitle')}
              </Text>
            </Box>
            
            {/* 错误提示 */}
            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <Text>{error}</Text>
              </Alert>
            )}
            
            {/* 可视化占位 - 主内容 */}
            {isLoading && !subscriptionData ? (
              <Flex justify="center" py={10}>
                <Spinner size="xl" color="#FF5A5F" />
              </Flex>
            ) : (
              <>
                {/* 当前订阅信息 - 如果存在活跃订阅，显示在顶部 */}
                {hasActiveSubscription && (
                  <Box mb={{ base: 6, md: 8 }}>
                    <Heading 
                      as="h2" 
                      fontSize={{ base: 'xl', md: '2xl' }}
                      mb={4}
                      fontWeight="500"
                    >
                      {t('v0/subscription:management.title')}
                    </Heading>
                    <SubscriptionManagement 
                      subscription={subscriptionData}
                      isLoading={isLoading}
                    />
                  </Box>
                )}
                
                {/* 订阅计划选择 */}
                <Box>
                  <Heading 
                    as="h2" 
                    fontSize={{ base: 'xl', md: '2xl' }}
                    mb={4}
                    fontWeight="500"
                    textAlign={{ base: 'left', md: 'center' }}
                  >
                    {t('v0/subscription:tabs.plans')}
                  </Heading>
                  <SubscriptionPlans 
                    isLoading={isLoading}
                    activeSubscription={subscriptionData}
                    onSelectPlan={handleSelectPlan}
                  />
                </Box>
              </>
            )}
          </VStack>
        </Container>
      </Box>
    </PageLayout>
  );
};

export default Subscription; 